import {
  getQuestionnaireDataMap,
  resolveQuestionIdForStep,
} from '../services/medicalQuestionnaire';
import {
  defaultYesNoChoiceOptions,
  inferYesNoDetailOnChoices,
  isOptionBasedQuestionType,
  isYesNoInputQuestionType,
  normalizeChoiceOptionsFromApi,
  rawOptionsHaveAuthoritativeIds,
  resolveStepMaxSelections,
} from './medicalQuestionnaireChoiceOptions';

/** Merge `options` from filled GET when list options lack authoritative ids. */
export function mergeOptionsIntoQuestionnairePayload(payload, optionsByQuestionId) {
  if (!optionsByQuestionId || typeof optionsByQuestionId !== 'object') {
    return payload;
  }
  if (!Object.keys(optionsByQuestionId).length) return payload;

  const data = getQuestionnaireDataMap(payload);
  const out = {};
  for (const [title, questions] of Object.entries(data)) {
    out[title] = (questions || []).map((question) => {
      const resolvedQid = resolveQuestionIdForStep(question);
      const questionId = Number.isFinite(resolvedQid) ? resolvedQid : null;
      if (questionId == null) return question;
      const filledOptions = optionsByQuestionId[questionId];
      if (!Array.isArray(filledOptions) || !filledOptions.length) return question;

      const listOptions = Array.isArray(question.options) ? question.options : [];
      const listHasIds = rawOptionsHaveAuthoritativeIds(listOptions);
      const filledHasIds = rawOptionsHaveAuthoritativeIds(filledOptions);
      if (filledHasIds && !listHasIds) {
        return { ...question, options: filledOptions };
      }
      if (!listOptions.length && filledOptions.length) {
        return { ...question, options: filledOptions };
      }
      return question;
    });
  }
  return out;
}

/** Merge `validation` from filled GET onto questions list when list omits rules. */
export function mergeValidationIntoQuestionnairePayload(payload, validationByQuestionId) {
  if (!validationByQuestionId || typeof validationByQuestionId !== 'object') {
    return payload;
  }
  if (!Object.keys(validationByQuestionId).length) return payload;

  const data = getQuestionnaireDataMap(payload);
  const out = {};
  for (const [title, questions] of Object.entries(data)) {
    out[title] = (questions || []).map((question) => {
      const resolvedQid = resolveQuestionIdForStep(question);
      const questionId = Number.isFinite(resolvedQid) ? resolvedQid : null;
      if (questionId == null) return question;
      const rules = validationByQuestionId[questionId];
      if (!rules || typeof rules !== 'object') return question;
      const hasValidation =
        question.validation &&
        typeof question.validation === 'object' &&
        !Array.isArray(question.validation) &&
        Object.keys(question.validation).length > 0;
      if (hasValidation) return question;
      return { ...question, validation: rules };
    });
  }
  return out;
}

function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

/** Detail label + helper for medication / allergy Yes_no_input steps (FE fallback). */
function resolveYesNoDetailCopy(questionText) {
  const q = String(questionText || '').toLowerCase();
  if (q.includes('medicijn')) {
    return {
      detailLabel: 'Welke?',
      detailHelperText:
        'Geef aan welke medicijnen je gebruikt op het moment van de bloedafname.',
    };
  }
  if (q.includes('allergie') || q.includes('intolerant')) {
    return {
      detailLabel: 'Welke?',
      detailHelperText:
        'Geef aan welke allergieën of intoleranties van toepassing zijn op het moment van de bloedafname.',
    };
  }
  return null;
}

/** Prefer API `detail_label` / `helper_text`; fall back to keyword match. */
function resolveYesNoDetailFields(question) {
  const apiLabel = String(question?.detail_label || '').trim();
  const apiHelper = String(question?.helper_text || '').trim();
  if (apiLabel || apiHelper) {
    return {
      ...(apiLabel ? { detailLabel: apiLabel } : {}),
      ...(apiHelper ? { detailHelperText: apiHelper } : {}),
    };
  }
  return resolveYesNoDetailCopy(question?.question) || null;
}

export function buildSectionsFromApi(payload) {
  const data = getQuestionnaireDataMap(payload);
  return Object.entries(data).map(([title, questions], sectionIdx) => {
    const mappedQuestions = [...(questions || [])]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map((question, questionIdx) => {
        const resolvedQid = resolveQuestionIdForStep(question);
        const questionId = Number.isFinite(resolvedQid) ? resolvedQid : null;
        const baseKey = slugify(question.question) || `q_${sectionIdx}_${questionIdx}`;
        const rawType = String(question.question_type || '')
          .toLowerCase()
          .replace(/[_\s]+/g, '-');
        const rawOptions = Array.isArray(question.options) ? question.options : [];
        const choiceOptions = normalizeChoiceOptionsFromApi(rawOptions);
        const options = choiceOptions.map((opt) => ({
          label: opt.label,
          value: opt.id,
          requiresDetail: opt.requiresDetail,
        }));
        const vObj =
          question.validation && typeof question.validation === 'object' && !Array.isArray(question.validation)
            ? question.validation
            : {};
        const placeholderOptional =
          String(question.place_holder || '').trim().toLowerCase() === 'optioneel';
        const rulesRequired = vObj.required === true;
        const explicitlyOptional =
          vObj.required === false || vObj.require === false;
        const isOptional =
          explicitlyOptional || (placeholderOptional && vObj.required !== true);
        const hasOptions = options.length > 0;
        const hasDetailInput =
          Boolean(question.has_detail_input) ||
          (hasOptions &&
            rawOptions.some((o) =>
              typeof o === 'object' && o != null
                ? Boolean(o.requires_input || o.requires_detail || o.has_input)
                : false
            ));
        const isYesNoInputType = isYesNoInputQuestionType(rawType);
        const multi = Boolean(
          question.allow_multiple ||
            question.multi ||
            rawType.includes('multiple') ||
            rawType === 'multiple-choice'
        );
        const minFromApi = question.min_value ?? vObj.min ?? vObj.minimum;
        const maxFromApi = question.max_value ?? vObj.max ?? vObj.maximum;
        const validationType = String(vObj.type || '').toLowerCase();
        const validation = {
          type:
            validationType ||
            (multi && (rawType.includes('multiple') || rawType === 'multiple-choice')
              ? 'array'
              : undefined),
          required: rulesRequired || undefined,
          min: Number.isFinite(Number(minFromApi)) ? Number(minFromApi) : undefined,
          max: Number.isFinite(Number(maxFromApi)) ? Number(maxFromApi) : undefined,
          integer:
            question.integer_only === true ||
            question.is_integer === true ||
            question.allow_decimal === false ||
            vObj.integer_only === true ||
            vObj.is_integer === true ||
            vObj.allow_decimal === false,
        };
        const maxSelections = resolveStepMaxSelections(question, validation);
        if (Number.isFinite(maxSelections) && !Number.isFinite(validation.max)) {
          validation.max = maxSelections;
        }
        const optionBased = hasOptions && isOptionBasedQuestionType(rawType);
        const usesOptionsAnswerId =
          optionBased || multi || validationType === 'array';

        if ((hasOptions && hasDetailInput) || isYesNoInputType) {
          let yesNoChoices =
            choiceOptions.length >= 2
              ? choiceOptions
              : isYesNoInputType
                ? defaultYesNoChoiceOptions()
                : choiceOptions;
          yesNoChoices = inferYesNoDetailOnChoices(yesNoChoices);
          const detailTriggerOptionIds = yesNoChoices
            .filter((o) => o.requiresDetail)
            .map((o) => o.id);
          const yesNoUsesOptionIds =
            yesNoChoices.length >= 2 &&
            (usesOptionsAnswerId ||
              validationType === 'array' ||
              isYesNoInputType ||
              rawOptionsHaveAuthoritativeIds(rawOptions));
          const detailCopy = resolveYesNoDetailFields(question);
          return {
            questionId,
            key: baseKey,
            question: question.question,
            type: 'yesNoWithTextarea',
            choiceOptions: yesNoChoices,
            yesNoUsesOptionIds,
            usesOptionsAnswerId: yesNoUsesOptionIds,
            detailTriggerOptionIds: detailTriggerOptionIds.length
              ? detailTriggerOptionIds
              : ['yes'],
            detailTriggerValues: yesNoChoices
              .filter((o) => o.requiresDetail)
              .map((o) => o.label),
            detailKey: `${baseKey}_details`,
            detailPlaceholder: question.place_holder || '',
            ...(detailCopy || {}),
            validation,
            optional: isOptional,
          };
        }

        if (hasOptions && optionBased) {
          return {
            questionId,
            key: baseKey,
            question: question.question,
            type: 'choiceList',
            choiceOptions,
            multi,
            optionBased: true,
            usesOptionsAnswerId,
            ...(maxSelections ? { maxSelections } : {}),
            validation,
            optional: isOptional,
          };
        }

        if (hasOptions) {
          return {
            questionId,
            key: baseKey,
            question: question.question,
            type: 'choiceList',
            choiceOptions,
            options: options.map((opt) => opt.label),
            multi,
            usesOptionsAnswerId,
            ...(maxSelections ? { maxSelections } : {}),
            validation,
            optional: isOptional,
          };
        }

        const treatInputAsNumber =
          (rawType === 'input' || rawType === 'number-input') &&
          !hasOptions &&
          (validation.integer ||
            Number.isFinite(validation.min) ||
            Number.isFinite(validation.max) ||
            /\b(cm|kg|mm|ml|mg)\b/i.test(String(question.question || '')) ||
            /\b(cm|kg|mm|ml|mg)\b/i.test(String(question.place_holder || '')));
        const resolvedType =
          rawType.includes('number') || treatInputAsNumber ? 'number' : 'text';
        return {
          questionId,
          key: baseKey,
          question: question.question,
          type: resolvedType,
          placeholder: question.place_holder || '',
          validation: treatInputAsNumber
            ? {
                ...validation,
                integer:
                  validation.integer === true ||
                  /\bkg\b/i.test(String(question.question || '')) ||
                  /\bkg\b/i.test(String(question.place_holder || '')),
              }
            : validation,
          optional: isOptional,
        };
      });

    return {
      title,
      questions: mappedQuestions,
    };
  });
}

export function flatStepsFromSections(sections) {
  return sections.flatMap((section, sectionIdx) =>
    section.questions.map((q, questionIdx) => ({
      ...q,
      sectionIdx,
      questionIdx,
      sectionTitle: section.title,
    }))
  );
}
